"use client";

import { useState } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphAnnouncementBar as ParagraphAnnouncementBarData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphAnnouncementBar.module.css";

/**
 * Layout 69 - `PARAGRAPH_ANNOUNCEMENT_BAR_LAYOUT`: dark full-bleed pasek ogloszen.
 *
 * Struktura:
 *   - Dark bg full-bleed (100vw przez box-shadow + clip-path)
 *   - Swiper single-slide z autoplay (autoplay_delay_ms z backendu)
 *   - Slajd: pill + Nucleo icon + tekst + arrow (caly klikalny gdy url)
 *   - Prawa strona: pagination bullets + progress ring
 */
export function ParagraphAnnouncementBar({
  paragraph,
}: {
  paragraph: ParagraphAnnouncementBarData;
  t: ParagraphTranslations;
}) {
  if (!Array.isArray(paragraph.items) || paragraph.items.length === 0) return null;

  const { items, autoplay_delay_ms } = paragraph;
  const isLoop = items.length > 1;
  //Kluczem sterujemy remount'em SVG ringa - po zmianie slajdu React tworzy nowy
  //element i CSS animation `dashoffset` startuje od 0 na kazdym slajdzie.
  const [progressKey, setProgressKey] = useState(0);

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.swiperWrap}>
          <Swiper
            modules={[Autoplay, Pagination, A11y]}
            slidesPerView={1}
            loop={isLoop}
            observer={true}
            observeParents={true}
            autoplay={isLoop ? { delay: autoplay_delay_ms, disableOnInteraction: false } : false}
            pagination={isLoop ? { clickable: true, el: `.${styles.pagination}` } : false}
            allowTouchMove={isLoop}
            spaceBetween={0}
            className={styles.swiper}
            onSlideChange={() => setProgressKey((k) => k + 1)}
          >
            {items.map((item) => (
              <SwiperSlide key={item.id} className={styles.slide}>
                <SlideContent item={item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {isLoop ? (
          <div className={styles.pagination} />
        ) : null}
        {isLoop ? (
          <ProgressRing key={progressKey} durationMs={autoplay_delay_ms} />
        ) : null}
      </div>
    </section>
  );
}

/**
 * Kolko z animowanym stroke-dashoffset - laduje sie w ciagu `durationMs` (rowny
 * autoplay delay) i po dojechaniu do konca resetuje. Remount przez zmiane
 * klucza w parent = kazdy nowy slajd startuje animacje od zera.
 */
function ProgressRing({ durationMs }: { durationMs: number }) {
  //r=9 -> obwod = 2*PI*9 ~= 56.55; dashoffset animowany od pelnego do 0.
  const circumference = 2 * Math.PI * 9;
  return (
    <svg
      className={styles.progressRing}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className={styles.progressTrack}
        cx="12"
        cy="12"
        r="9"
      />
      <circle
        className={styles.progressFill}
        cx="12"
        cy="12"
        r="9"
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: circumference,
          animationDuration: `${durationMs}ms`,
        }}
      />
    </svg>
  );
}

function SlideContent({ item }: { item: ParagraphAnnouncementBarData["items"][number] }) {
  const inner = (
    <>
      {item.pill ? <span className={styles.pill}>{item.pill}</span> : null}
      {item.icon ? (
        <span className={styles.icon} aria-hidden="true">
          <i className={`nc-icon-outline ${item.icon}`} />
        </span>
      ) : null}
      {item.text ? <span className={styles.text}>{item.text}</span> : null}
      {item.url ? <span className={styles.arrow} aria-hidden="true" /> : null}
    </>
  );

  if (!item.url) {
    return <div className={styles.slideLink}>{inner}</div>;
  }

  const href = extractInternalHref(item.url) ?? item.url;
  return (
    <Link href={href} className={styles.slideLink}>
      {inner}
    </Link>
  );
}
